import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet, Eye, EyeOff, CheckCircle2, Circle, Mail, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { getSafeRedirectPath } from '../lib/authRedirect';
import { getPasswordRuleStates, isPasswordValid } from '../lib/passwordRules';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

type AuthView = 'login' | 'signup' | 'forgot-password' | 'update-password';

const getViewFromParams = (searchParams: URLSearchParams): AuthView => {
  const mode = searchParams.get('mode');

  if (mode === 'signup') return 'signup';
  if (mode === 'forgot-password') return 'forgot-password';
  if (mode === 'update-password') return 'update-password';
  return 'login';
};

const getNoticeFromParams = (searchParams: URLSearchParams) => {
  if (searchParams.get('confirmed') === '1') {
    return 'Email confirmed. You can sign in now.';
  }

  if (searchParams.get('passwordUpdated') === '1') {
    return 'Password updated. Sign in with your new password.';
  }

  return '';
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    login,
    signup,
    logout,
    requestPasswordReset,
    resendConfirmation,
    restoreRecoverySession,
    updatePassword,
  } = useAuthStore();
  const inviteSpaceName = searchParams.get('inviteSpaceName');
  const redirectPath = useMemo(
    () => getSafeRedirectPath(searchParams.get('redirect'), '/'),
    [searchParams]
  );
  const [view, setView] = useState<AuthView>(getViewFromParams(searchParams));
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(getNoticeFromParams(searchParams));
  const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState('');

  const passwordRuleStates = getPasswordRuleStates(formData.password);
  const showPasswordChecks =
    (view === 'signup' || view === 'update-password') && formData.password.length > 0;
  const passwordsMatch =
    formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;
  const confirmPasswordError =
    (view === 'signup' || view === 'update-password') &&
    formData.confirmPassword.length > 0 &&
    !passwordsMatch
      ? 'Passwords do not match'
      : undefined;

  useEffect(() => {
    const nextView = getViewFromParams(searchParams);
    const inviteDisplayName = searchParams.get('displayName') || '';
    const authError = searchParams.get('authError');

    setView(nextView);
    setError(authError ? decodeURIComponent(authError) : '');
    setNotice(getNoticeFromParams(searchParams));
    setFormData((prev) => ({
      ...prev,
      displayName: prev.displayName || inviteDisplayName,
    }));
  }, [searchParams]);

  useEffect(() => {
    if (view !== 'update-password') return;

    void restoreRecoverySession().catch((err: any) => {
      setError(err?.message || 'We could not restore your password reset session. Please request a new link.');
    });
  }, [restoreRecoverySession, view]);

  const setLocalView = (nextView: AuthView) => {
    setView(nextView);
    setError('');
    setNotice('');
  };

  const resetPasswordFields = () => {
    setFormData((prev) => ({
      ...prev,
      password: '',
      confirmPassword: '',
    }));
    setShowPassword(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (view === 'signup' || view === 'update-password') {
      if (!isPasswordValid(formData.password)) {
        setError(
          view === 'signup'
            ? 'Choose a stronger password before creating your account.'
            : 'Choose a stronger password before updating it.'
        );
        return;
      }

      if (!passwordsMatch) {
        setError('Confirm your password so both fields match.');
        return;
      }
    }

    setIsLoading(true);

    try {
      if (view === 'login') {
        await login(formData.email, formData.password);
        navigate(redirectPath);
        return;
      }

      if (view === 'signup') {
        const signupResult = await signup(formData.email, formData.password, formData.displayName);
        if (signupResult === 'confirmation_required') {
          setPendingConfirmationEmail(formData.email);
          setNotice('Check your email to confirm your account before signing in.');
          resetPasswordFields();
          setView('login');
          return;
        }

        navigate(redirectPath);
        return;
      }

      if (view === 'forgot-password') {
        await requestPasswordReset(formData.email);
        setNotice('Check your email for a password reset link.');
        setView('login');
        return;
      }

      const hasRecoverySession = await restoreRecoverySession();
      if (!hasRecoverySession) {
        throw new Error('Your reset link expired. Please request a new password reset email.');
      }

      await updatePassword(formData.password);
      await logout();
      resetPasswordFields();
      navigate('/login?passwordUpdated=1', { replace: true });
    } catch (err: any) {
      const message = err.message || 'Something went wrong';
      if (/confirm your email/i.test(message)) {
        setPendingConfirmationEmail(formData.email);
      }

      setError(
        /email rate limit exceeded|over_email_send_rate_limit/i.test(message)
          ? 'Too many auth emails were sent recently. Please wait a moment, then try again.'
          : message === 'Failed to fetch'
            ? 'Authentication service could not be reached. Please try again in a moment.'
            : message
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!pendingConfirmationEmail) return;

    setIsLoading(true);
    setError('');

    try {
      await resendConfirmation(pendingConfirmationEmail);
      setNotice(`Confirmation email resent to ${pendingConfirmationEmail}.`);
    } catch (err: any) {
      setError(err.message || 'We could not resend the confirmation email.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeading = () => {
    if (inviteSpaceName) {
      if (view === 'signup') return `Create your account to join ${inviteSpaceName}.`;
      if (view === 'forgot-password') return `Reset your password to get back into ${inviteSpaceName}.`;
      if (view === 'update-password') return 'Choose a new password to finish recovery.';
      return `Sign in to join ${inviteSpaceName}.`;
    }

    if (view === 'signup') return 'Create your account and start tracking together.';
    if (view === 'forgot-password') return 'We’ll send you a secure link to reset your password.';
    if (view === 'update-password') return 'Set a new password for your PairTab account.';
    return 'Track together. Split smart.';
  };

  const submitLabel =
    view === 'signup'
      ? 'Create Account'
      : view === 'forgot-password'
        ? 'Send Reset Link'
        : view === 'update-password'
          ? 'Update Password'
          : 'Sign In';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Wallet className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">PairTab</h1>
          <p className="text-muted">{renderHeading()}</p>
        </div>

        {(notice || error) && (
          <div className="mb-4 space-y-3">
            {notice && (
              <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
                {notice}
              </div>
            )}

            {error && (
              <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {view === 'signup' && (
            <Input
              label="Display Name"
              placeholder="e.g., Alex"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              required
            />
          )}

          {view !== 'update-password' && (
            <Input
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          )}

          {view !== 'forgot-password' && (
            <div className="relative">
              <Input
                label={view === 'update-password' ? 'New Password' : 'Password'}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[38px] text-muted"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          )}

          {(view === 'signup' || view === 'update-password') && (
            <>
              {showPasswordChecks && (
                <div className="rounded-xl border border-border bg-card/60 p-3 space-y-2">
                  <p className="text-xs font-medium text-foreground">Password must include:</p>
                  {passwordRuleStates.map((rule) => (
                    <div
                      key={rule.id}
                      className={`flex items-center gap-2 text-sm ${
                        rule.met ? 'text-primary' : 'text-muted'
                      }`}
                    >
                      {rule.met ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                      <span>{rule.label}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="relative">
                <Input
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Repeat your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  error={confirmPasswordError}
                  required
                />
                {passwordsMatch && (
                  <CheckCircle2
                    size={18}
                    className="absolute right-4 top-[41px] text-primary"
                    aria-hidden="true"
                  />
                )}
              </div>

              {passwordsMatch && (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <CheckCircle2 size={16} />
                  <span>Passwords match</span>
                </div>
              )}
            </>
          )}

          <Button
            type="submit"
            disabled={
              isLoading ||
              ((view === 'signup' || view === 'update-password') &&
                (!isPasswordValid(formData.password) || !passwordsMatch))
            }
            className="w-full"
          >
            {isLoading ? 'Loading...' : submitLabel}
          </Button>
        </form>

        {view === 'login' && (
          <div className="mt-4 space-y-3 text-center">
            <button
              type="button"
              onClick={() => setLocalView('forgot-password')}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <Mail size={14} />
              Forgot your password?
            </button>

            {pendingConfirmationEmail && (
              <div className="space-y-2">
                <p className="text-xs text-muted">Still waiting on the confirmation email?</p>
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  className="text-sm font-medium text-primary hover:underline"
                  disabled={isLoading}
                >
                  Resend confirmation email
                </button>
              </div>
            )}
          </div>
        )}

        {(view === 'forgot-password' || view === 'update-password') && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                resetPasswordFields();
                setLocalView('login');
              }}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <ArrowLeft size={14} />
              Back to sign in
            </button>
          </div>
        )}

        {(view === 'login' || view === 'signup') && (
          <p className="text-center mt-6 text-sm text-muted">
            {view === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => setLocalView(view === 'login' ? 'signup' : 'login')}
              className="text-primary font-medium hover:underline"
            >
              {view === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        )}
      </motion.div>
    </div>
  );
}
